import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from '../services/authService';
import { useAuthStore } from '../store/authStore';
import { useToastStore } from '../store/toastStore';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { FormField } from '../components/ui/FormField';
import { StarIcon } from '../components/ui/Icons';

const BRAND_BENEFITS = [
  { label: 'Pedidos na palma da mão', description: 'Acompanhe o status de tudo o que você comprou' },
  { label: 'Compra rápida e segura', description: 'Seus dados protegidos em todas as etapas' },
  { label: 'Ofertas exclusivas', description: 'Novidades e promoções para quem já é da família' },
];

export function LoginPage() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const showToast = useToastStore((s) => s.show);

  const [credential, setCredential] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await authService.login({ credential, password });
      login(data.accessToken, data.customerId, data.name, data.isAdmin);
      navigate('/');
    } catch (err: any) {
      showToast(err?.response?.data?.errors?.[0] ?? 'Credenciais inválidas.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex">
      {/* Painel esquerdo — apresentação da marca (oculto em mobile) */}
      <div
        className="hidden lg:flex flex-col justify-between w-[46%] px-12 py-14"
        style={{ background: 'linear-gradient(145deg, #f9e8d0 0%, #f2dbc0 50%, #e8ccad 100%)' }}
      >
        {/* Logo */}
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-3xl">🦄</span>
            <span className="text-2xl font-bold tracking-wide text-text-dark">BabyChic</span>
          </div>
          <p className="text-sm text-text-medium tracking-widest uppercase font-medium ml-1">
            Vestimos criança como criança!
          </p>
        </div>

        {/* Tagline central */}
        <div className="flex-1 flex flex-col justify-center">
          <h2 className="text-4xl font-bold text-text-dark leading-snug mb-4">
            Bem-vinda<br />
            <span className="text-chocolate">de volta</span>
          </h2>
          <p className="text-text-medium text-base leading-relaxed max-w-xs">
            Entre na sua conta e acompanhe seus pedidos com facilidade, a qualquer hora e em qualquer lugar.
          </p>
        </div>

        {/* Benefícios */}
        <div className="grid grid-cols-1 gap-4">
          {BRAND_BENEFITS.map((b) => (
            <div key={b.label} className="flex items-start gap-3">
              <StarIcon className="text-chocolate mt-0.5 w-3.5 h-3.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-text-dark">{b.label}</p>
                <p className="text-xs text-text-light">{b.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Rodapé do painel */}
        <p className="text-xs text-text-light mt-8">
          © 2025 BabyChic — Todos os direitos reservados
        </p>
      </div>

      {/* Painel direito — formulário */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-champagne-light">
        <div className="w-full max-w-md">
          {/* Logo mobile */}
          <div className="flex lg:hidden items-center gap-2 mb-8 justify-center">
            <span className="text-2xl">🦄</span>
            <span className="text-xl font-bold text-text-dark">BabyChic</span>
          </div>

          {/* Cabeçalho do form */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-text-dark mb-1">Entrar</h1>
            <p className="text-text-light text-sm">
              Use seu e-mail ou telefone para acessar sua conta
            </p>
          </div>

          {/* Formulário */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <FormField label="E-mail ou telefone" required>
              <Input
                type="text"
                value={credential}
                onChange={(e) => setCredential(e.target.value)}
                placeholder="exemplo@email.com ou 85994322348"
                required
                autoComplete="username"
              />
            </FormField>

            <FormField label="Senha" required>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
              />
            </FormField>

            <div className="text-right">
              <Link to="/esqueci-senha" className="text-chocolate hover:underline text-sm">
                Esqueci minha senha
              </Link>
            </div>

            <Button type="submit" fullWidth loading={loading} size="lg" className="mt-1">
              Entrar
            </Button>
          </form>

          {/* Link para cadastro */}
          <p className="text-center text-sm text-text-light mt-6">
            Primeira vez?{' '}
            <Link to="/cadastro" className="text-chocolate font-medium hover:underline">
              Criar acesso
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
